import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Form,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  ButtonGroup,
} from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaToggleOn,
  FaToggleOff,
  FaCheck,
  FaTimes,
} from "react-icons/fa";

import {
  getCommunicationRules,
  createCommunicationRule,
  updateCommunicationRule,
  deleteCommunicationRule,
  toggleCommunicationRuleActive,
  getCommunicationRulesByAssessment,
  getAssessmentScreener,
} from "../services/APIClient";

function ManageRulesModal({ show, onHide, onRuleUpdated }) {
  const [rules, setRules] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    assessmentId: "",
    eventType: "",
    threshold: "",
    recipientRole: "Alpaca Lead",
    active: true,
  });

  // Available options
  const eventTypes = [
    { value: "screening-completion", label: "Screening Completion Milestone" },
    { value: "screening-overdue", label: "Screening Overdue Alert" },
    { value: "screening-reminder", label: "Screening Progress Reminder" },
    { value: "assessment-completion", label: "Assessment Completion" },
  ];

  const recipientRoles = [
    "Alpaca Lead",
    "School Principal",
    "Vice Principal",
    "Department Head",
  ];

  useEffect(() => {
    if (show) {
      loadRules();
      loadAssessments();
    }
  }, [show]);

  const loadRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCommunicationRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load rules: " + err.message);
      setRules([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadAssessments = async () => {
    setLoadingAssessments(true);
    try {
      // Get all active assessments
      const data = await getAssessmentScreener({ active: true });
      setAssessments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load assessments:", err);
      setAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const handleToggleActive = async (ruleId) => {
    try {
      await toggleCommunicationRuleActive(ruleId);
      setSuccess("Rule status updated successfully");
      loadRules();
      if (onRuleUpdated) onRuleUpdated();
    } catch (err) {
      setError("Failed to toggle rule: " + err.message);
    }
  };

  const handleDelete = async (ruleId, rule) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the rule for "${rule.eventType}"?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteCommunicationRule(ruleId);
      setSuccess("Rule deleted successfully");
      loadRules();
      if (onRuleUpdated) onRuleUpdated();
    } catch (err) {
      setError("Failed to delete rule: " + err.message);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      assessmentId: rule.assessmentId,
      eventType: rule.eventType,
      threshold: rule.threshold,
      recipientRole: rule.recipientRole,
      active: rule.active,
    });
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingRule(null);
    setFormData({
      assessmentId: "",
      eventType: "",
      threshold: "",
      recipientRole: "Alpaca Lead",
      active: true,
    });
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.assessmentId || !formData.eventType || !formData.threshold) {
      setError("Please fill in all required fields");
      return;
    }

    const thresholdNum = parseFloat(formData.threshold);
    
    if (isNaN(thresholdNum) || thresholdNum < 0 || thresholdNum > 100) {
      setError("Threshold must be a number between 0 and 100");
      return;
    }

    try {
      const ruleData = {
        assessmentId: formData.assessmentId,
        eventType: formData.eventType,
        threshold: thresholdNum, // Send as number
        recipientRole: formData.recipientRole,
        active: formData.active,
      };

      if (editingRule) {
        // Update existing rule
        await updateCommunicationRule(editingRule.id, ruleData);
        setSuccess("Rule updated successfully");
      } else {
        // Create new rule
        await createCommunicationRule(ruleData);
        setSuccess("Rule created successfully");
      }

      setShowForm(false);
      setEditingRule(null);
      loadRules();
      if (onRuleUpdated) onRuleUpdated();
    } catch (err) {
      setError(
        `Failed to ${editingRule ? "update" : "create"} rule: ${err.message}`
      );
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingRule(null);
    setError(null);
  };

  const getEventTypeDisplay = (eventType) => {
    const type = eventTypes.find(t => t.value === eventType);
    return type ? type.label : eventType;
  };

  const getAssessmentDisplay = (assessmentId) => {
    const assessment = assessments.find(a => a.id === assessmentId);
    if (assessment) {
      return assessment.name;
    }
    return assessmentId; // Fallback to ID if not found
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {showForm
            ? editingRule
              ? "Edit Communication Rule"
              : "Create New Communication Rule"
            : "Manage Communication Rules"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Success/Error Messages */}
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Form View */}
        {showForm ? (
          <Form onSubmit={handleFormSubmit}>
            <Alert variant="info" className="mb-3">
              <strong>How it works:</strong> When a school reaches the specified threshold percentage for an assessment, 
              the system will send notifications to the designated recipient role. For example, a "Screening Completion Milestone" 
              event at 75% threshold will notify when 75% of students have completed the screening.
            </Alert>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Assessment <span className="text-danger">*</span>
                  </Form.Label>
                  {loadingAssessments ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                      <small className="text-muted ms-2">Loading assessments...</small>
                    </div>
                  ) : (
                    <Form.Select
                      value={formData.assessmentId}
                      onChange={(e) =>
                        setFormData({ ...formData, assessmentId: e.target.value })
                      }
                      required
                    >
                      <option value="">Select an assessment...</option>
                      {assessments.map((assessment) => (
                        <option key={assessment.id} value={assessment.id}>
                          {assessment.name} {assessment.grade_level ? `(Grade ${assessment.grade_level})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                  <Form.Text className="text-muted">
                    The assessment this rule applies to
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Event Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.eventType}
                    onChange={(e) =>
                      setFormData({ ...formData, eventType: e.target.value })
                    }
                    required
                  >
                    <option value="">Select event type...</option>
                    {eventTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Type of notification to send
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Threshold (%) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.threshold}
                    onChange={(e) =>
                      setFormData({ ...formData, threshold: e.target.value })
                    }
                    placeholder="e.g., 75"
                    required
                  />
                  <Form.Text className="text-muted">
                    Trigger when this percentage is reached (e.g., 75 for 75% completion)
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Recipient Role <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.recipientRole}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        recipientRole: e.target.value,
                      })
                    }
                    required
                  >
                    {recipientRoles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Who will receive notifications
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active (rule will be evaluated during threshold checks)"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCancelForm}>
                <FaTimes style={{ marginRight: "0.5rem" }} />
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                <FaCheck style={{ marginRight: "0.5rem" }} />
                {editingRule ? "Update Rule" : "Create Rule"}
              </Button>
            </div>
          </Form>
        ) : (
          <>
            {/* Table View */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-0">
                  Communication Rules ({rules.length})
                </h5>
                <small className="text-muted">
                  Manage automated notification triggers
                </small>
              </div>
              <Button variant="primary" onClick={handleCreateNew}>
                <FaPlus style={{ marginRight: "0.5rem" }} />
                Create New Rule
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="text-muted mt-2">Loading rules...</p>
              </div>
                          ) : rules.length === 0 ? (
              <Alert variant="info">
                No communication rules configured yet. Click "Create New Rule"
                to get started.
              </Alert>
            ) : (
              <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                <Table striped bordered hover responsive>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                    <tr>
                      <th>Status</th>
                      <th>Assessment</th>
                      <th>Event Type</th>
                      <th>Threshold</th>
                      <th>Recipient Role</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(rules) && rules.map((rule) => (
                      <tr key={rule.id}>
                        <td>
                          <Badge bg={rule.active ? "success" : "secondary"}>
                            {rule.active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td>
                          <strong>{getAssessmentDisplay(rule.assessmentId)}</strong>
                          <div className="text-muted small">
                            <code>{rule.assessmentId}</code>
                          </div>
                        </td>
                        <td>
                          {getEventTypeDisplay(rule.eventType)}
                          <div className="text-muted small">
                            Triggers at {rule.threshold}%
                          </div>
                        </td>
                        <td>
                          <strong>{rule.threshold}%</strong>
                        </td>
                        <td>{rule.recipientRole}</td>
                        <td>
                          <small>
                            {new Date(rule.createdAt).toLocaleDateString()}
                          </small>
                          {rule.creator && (
                            <div className="text-muted small">
                              by {rule.creator.name}
                            </div>
                          )}
                        </td>
                        <td>
                          <ButtonGroup size="sm">
                            <Button
                              variant={rule.active ? "warning" : "success"}
                              onClick={() => handleToggleActive(rule.id)}
                              title={
                                rule.active
                                  ? "Deactivate rule"
                                  : "Activate rule"
                              }
                            >
                              {rule.active ? (
                                <FaToggleOff />
                              ) : (
                                <FaToggleOn />
                              )}
                            </Button>
                            <Button
                              variant="info"
                              onClick={() => handleEdit(rule)}
                              title="Edit rule"
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => handleDelete(rule.id, rule)}
                              title="Delete rule"
                            >
                              <FaTrash />
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!showForm && (
          <>
            <div className="me-auto text-muted">
              <small>
                Rules are evaluated daily or when manually triggered
              </small>
            </div>
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ManageRulesModal;